import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Lock, LogOut, Radiation, Skull, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestsPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("rotu_guest_token") || null);
  const [password, setPassword] = useState("");
  const [guests, setGuests] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/rsvps`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setGuests(r.data))
      .catch(() => {
        sessionStorage.removeItem("rotu_guest_token");
        setToken(null);
      });
  }, [token]);

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/guests/unlock`, { password });
      sessionStorage.setItem("rotu_guest_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError("Wrong password. The colony does not know you.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("rotu_guest_token");
    setToken(null);
    setGuests(null);
    setPassword("");
  };

  const heads = guests ? guests.reduce((sum, g) => sum + (g.crew_size || 1), 0) : 0;

  const exportCsv = () => {
    if (!guests || !guests.length) return;
    const rows = [["#", "Name", "Crew", "Entry Code", "Rat-Digits", "Signed"]];
    guests.forEach((g, i) =>
      rows.push([
        i + 1,
        g.alias,
        g.crew_size,
        g.entry_code,
        g.contact || "",
        g.created_at ? new Date(g.created_at).toLocaleString() : "",
      ])
    );
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "rotu-guestlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#08080C] text-white font-mono" data-testid="guests-page">
      <div className="grain-overlay" />
      <div className="scanlines" />
      <div className="vignette-overlay" />

      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radiation size={18} className="text-[#39FF14] animate-[spin_8s_linear_infinite]" />
          <span className="font-display text-sm font-bold tracking-widest">R.O.T.U. // GATEKEEPER</span>
        </div>
        <a href="/" className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-[#39FF14] transition-colors" data-testid="guests-back-link">
          Back to the pit
        </a>
      </header>

      {!token ? (
        <div className="flex items-center justify-center px-6 py-32">
          <form
            onSubmit={unlock}
            className="w-full max-w-sm border border-white/10 bg-[#111118]/80 backdrop-blur-xl p-10 text-center"
            data-testid="guests-gate"
          >
            <Skull size={28} className="mx-auto text-[#FF0055] mb-6" />
            <h1 className="font-display text-xl font-bold uppercase tracking-tight mb-2">
              Guest list
            </h1>
            <p className="text-xs text-zinc-500 mb-8">
              This list is for the gatekeeper only. Speak the password.
            </p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="password"
              data-testid="guests-password-input"
              className="w-full bg-[#08080C] border border-white/15 focus:border-[#39FF14] outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-700 transition-colors duration-300 mb-4"
            />
            {error && (
              <p className="text-xs text-[#FF0055] mb-4" data-testid="guests-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="guests-unlock-button"
              className="w-full py-3 border border-[#39FF14] text-[#39FF14] font-display text-xs font-bold uppercase tracking-widest hover:bg-[#39FF14] hover:text-[#08080C] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Lock size={12} /> {loading ? "Checking..." : "Unlock"}
            </button>
          </form>
        </div>
      ) : (
        <main className="max-w-4xl mx-auto px-6 py-14">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">
                The <span className="text-[#39FF14]">colony</span> list
              </h1>
              <p className="text-xs text-zinc-500 mt-2" data-testid="guests-summary">
                {guests ? `${guests.length} RSVPs // ${heads} heads expected` : "Summoning..."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportCsv}
                data-testid="guests-export-csv"
                className="flex items-center gap-2 px-4 py-2 border border-[#39FF14]/50 text-[10px] tracking-[0.3em] uppercase text-[#39FF14] hover:bg-[#39FF14] hover:text-[#08080C] transition-colors"
              >
                <Download size={12} /> Export CSV
              </button>
              <button
                onClick={logout}
                data-testid="guests-logout-button"
                className="flex items-center gap-2 px-4 py-2 border border-white/15 text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:border-[#FF0055] hover:text-[#FF0055] transition-colors"
              >
                <LogOut size={12} /> Lock
              </button>
            </div>
          </div>

          {guests === null ? (
            <p className="text-zinc-500 text-sm">Summoning the list...</p>
          ) : guests.length === 0 ? (
            <p className="text-zinc-500 text-sm">No one has sworn the oath yet.</p>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10 bg-[#111118]/60" data-testid="guest-list-table">
              <div className="hidden sm:grid grid-cols-[3rem_1fr_4rem_8rem_1fr_9rem] gap-3 px-5 py-3 text-[9px] tracking-[0.3em] uppercase text-zinc-600">
                <span>#</span>
                <span>Alias</span>
                <span>Crew</span>
                <span>Entry code</span>
                <span>Rat-digits</span>
                <span>Signed</span>
              </div>
              {guests.map((g, i) => (
                <div
                  key={g.entry_code || i}
                  className="grid sm:grid-cols-[3rem_1fr_4rem_8rem_1fr_9rem] gap-1 sm:gap-3 px-5 py-4 text-sm hover:bg-[#39FF14]/5 transition-colors"
                  data-testid={`guest-row-${i}`}
                >
                  <span className="font-display text-[#FF0055] text-xs">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-white font-bold flex items-center gap-2">
                    <Users size={12} className="text-zinc-600 shrink-0" />
                    {g.alias}
                  </span>
                  <span className="text-zinc-400 tabular-nums">x{g.crew_size}</span>
                  <span className="font-display text-[#39FF14] text-xs tracking-wider">{g.entry_code}</span>
                  <span className="text-zinc-500 text-xs truncate">{g.contact || "-"}</span>
                  <span className="text-zinc-600 text-xs">
                    {g.created_at ? new Date(g.created_at).toLocaleString() : "-"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </main>
      )}
    </div>
  );
}
